import { algoliasearch } from 'algoliasearch';
import { liteClient as createLiteClient } from 'algoliasearch/lite';

// Environment variables
const ALGOLIA_APP_ID = import.meta.env.VITE_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_KEY;
const ALGOLIA_WRITE_API_KEY = import.meta.env.ALGOLIA_WRITE_API_KEY;

// Check if credentials are available
const hasCredentials = !!(ALGOLIA_APP_ID && ALGOLIA_SEARCH_KEY);

if (!hasCredentials) {
  console.warn('⚠️ Missing Algolia credentials. Algolia search features will not work.');
}

/**
 * Full Algolia client for admin operations
 * Use this for server-side operations, index configuration, and data imports
 * DO NOT use in frontend code (exposes admin API key)
 */
export const algoliaAdminClient = typeof window === 'undefined' && ALGOLIA_WRITE_API_KEY && hasCredentials
  ? algoliasearch(ALGOLIA_APP_ID, ALGOLIA_WRITE_API_KEY)
  : null;

/**
 * Lightweight Algolia client for frontend searches
 * Smaller bundle size, optimized for search operations only
 * Use this in React components for search UI
 * Returns null if credentials are not available
 */
export const algoliaSearchClient = hasCredentials
  ? createLiteClient(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
  : null;

/**
 * Algolia client with analytics tracking
 * Wraps the lite client with click and conversion tracking
 * Automatically manages user tokens for personalization
 * Returns null if credentials are not available
 */
export const createAlgoliaClientWithAnalytics = (userId?: string) => {
  if (!algoliaSearchClient) {
    console.warn('⚠️ Cannot create analytics client: Algolia credentials missing');
    return null;
  }

  // Generate anonymous user token if no user ID provided
  const userToken = userId || `anonymous_${Date.now()}_${Math.random().toString(36).substring(7)}`;

  return {
    ...algoliaSearchClient,
    userToken,

    /**
     * Track click event for analytics
     * Call this when user clicks a search result
     */
    trackClick: (
      indexName: string,
      objectID: string,
      position: number,
      queryID: string
    ) => {
      // Algolia Insights API call will be added here
      console.log('Track click:', { indexName, objectID, position, queryID, userToken });
    },

    /**
     * Track conversion event for analytics
     * Call this when user downloads a sequence
     */
    trackConversion: (
      indexName: string,
      objectID: string,
      queryID?: string
    ) => {
      // Algolia Insights API call will be added here
      console.log('Track conversion:', { indexName, objectID, queryID, userToken });
    },
  };
};

/**
 * Default search client for InstantSearch
 * Can be replaced with analytics-enabled client when user is authenticated
 * Returns null if credentials are not available
 */
export const defaultSearchClient = algoliaSearchClient;

/**
 * Index names
 */
export const ALGOLIA_INDICES = {
  PRIMARY: 'sequences',
  DOWNLOAD_COUNT_DESC: 'sequences_download_count_desc',
  CREATED_AT_DESC: 'sequences_created_at_desc',
  PRICE_ASC: 'sequences_price_asc',
  PRICE_DESC: 'sequences_price_desc',
} as const;

export type AlgoliaIndexName = typeof ALGOLIA_INDICES[keyof typeof ALGOLIA_INDICES];
